import { useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import ReasonDialog from "../../components/admin/ReasonDialog";
import AccessDialog from "../../components/admin/AccessDialog";
import { fmtDate } from "../../lib/labels";
import { usePlatformCan } from "../../lib/platform";

const AddAdmin = ({ roles, onDone }) => {
  const [f, setF] = useState({ name: "", email: "", role: "support" });
  const [ask, setAsk] = useState(false);
  const submit = async (reason) => {
    try { await api.post("/admin/internal/admins", { ...f, reason }); toast.success("Accès interne attribué — un email a été envoyé"); setAsk(false); setF({ name: "", email: "", role: "support" }); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <form className="card-surface mb-6 grid gap-2 p-4 md:grid-cols-[1fr_1fr_200px_auto]" onSubmit={(e) => { e.preventDefault(); setAsk(true); }} data-testid="admin-internal-add-form">
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nom complet" required data-testid="admin-internal-name-input" />
      <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" required data-testid="admin-internal-email-input" />
      <select className="field-select" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="admin-internal-role-select">{roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select>
      <Button type="submit" data-testid="admin-internal-add-submit"><UserPlus className="mr-1 h-4 w-4" />Ajouter</Button>
      {ask && <ReasonDialog testId="admin-internal-add" title={`Attribuer un accès interne à ${f.email}`} label="Attribuer" variant="default" placeholder="Motif de l'attribution (journalisé)" onConfirm={submit} onClose={() => setAsk(false)} />}
    </form>
  );
};

const RoleMatrix = ({ catalog }) => (
  <div className="card-surface mt-8 overflow-x-auto" data-testid="admin-role-matrix">
    <table className="w-full min-w-[900px] text-xs">
      <thead className="border-b bg-slate-50 text-left uppercase text-slate-500"><tr><th className="px-3 py-2">Module / action</th>{catalog.roles.map((r) => <th key={r.key} className="px-2 py-2 text-center">{r.label}</th>)}</tr></thead>
      <tbody className="divide-y">{catalog.catalog.flatMap((m) => m.actions.map((a) => (
        <tr key={a.key}><td className="px-3 py-1.5"><b>{m.label}</b> · {a.label}</td>{catalog.roles.map((r) => <td key={r.key} className="text-center">{r.permissions.includes(a.key) ? "●" : "—"}</td>)}</tr>)))}</tbody>
    </table>
  </div>
);

export default function AdminInternal() {
  const { user: me } = useAuth();
  const can = usePlatformCan();
  const catalog = useApi("/admin/internal/catalog");
  const { data, loading, error, reload } = useApi("/admin/internal/admins");
  const [edit, setEdit] = useState(null);
  const [revoke, setRevoke] = useState(null);
  const doRevoke = async (reason) => {
    try { await api.post(`/admin/internal/admins/${revoke.id}/revoke`, { reason }); toast.success("Accès interne retiré"); setRevoke(null); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (loading || catalog.loading) return <Loader />;
  if (error || catalog.error) return <ErrorState message={error || catalog.error} onRetry={reload} />;
  const manage = can("admins:manage");
  return (
    <div>
      <PageHeader title="Administration interne" description="Équipe VOXA AFRIKA : rôles, accès par module et par action. Contrôlé côté serveur et journalisé." />
      {manage && <AddAdmin roles={catalog.data.roles} onDone={reload} />}
      <div className="card-surface overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">Membre</th><th className="px-4 py-3">Rôle</th><th className="px-4 py-3">Accès</th><th className="px-4 py-3">Depuis</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y">{data.map((a) => (
            <tr key={a.id} data-testid={`admin-internal-row-${a.id}`}>
              <td className="px-4 py-3"><p className="font-medium">{a.name}</p><p className="text-xs text-slate-500">{a.email}{a.is_active ? "" : " · désactivé"}</p></td>
              <td className="px-4 py-3">{a.role_label}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{a.permissions.length} droits{a.access.granted?.length ? ` · +${a.access.granted.length}` : ""}{a.access.revoked?.length ? ` · −${a.access.revoked.length}` : ""}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(a.internal_since || a.created_at, false)}</td>
              <td className="space-x-1 whitespace-nowrap px-4 py-3 text-right">{manage && a.id !== me.id && <>
                <Button size="sm" variant="outline" onClick={() => setEdit(a)} data-testid={`admin-internal-edit-${a.id}`}>Rôle & accès</Button>
                <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => setRevoke(a)} data-testid={`admin-internal-revoke-${a.id}`}>Retirer</Button>
              </>}{a.id === me.id && <span className="text-xs text-slate-400">vous</span>}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <RoleMatrix catalog={catalog.data} />
      {edit && <AccessDialog target={edit} catalog={catalog.data} onClose={() => setEdit(null)} onDone={() => { setEdit(null); reload(); }} />}
      {revoke && <ReasonDialog testId="admin-internal-revoke" title={`Retirer l'accès interne de ${revoke.name}`} label="Retirer" placeholder="Motif (journalisé)" onConfirm={doRevoke} onClose={() => setRevoke(null)} />}
    </div>
  );
}
